import React, { useState, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import style from './DriveThruInfo.module.scss';
import { getObjectValue } from '@utility/utills';
import { setErrorPopUp } from '@reducers/notification';
import LaneSelection from './LaneInfo';
import VehicleInfo from './VehicleInfo';
import ColorInfo from './ColorInfo';

const STORAGE_KEYS = {
  SELECTED_LANE: 'driveThru_selectedLane',
  SELECTED_VEHICLE_TYPE: 'driveThru_selectedVehicleType',
  SELECTED_VEHICLE_COLOR: 'driveThru_selectedVehicleColor',
  CURRENT_CHECK_NUMBER: 'driveThru_currentCheckNumber',
};

const DriveThruInfoV2 = ({
  check,
  onDriveThruSelectionChange,
  serverAppTheme,
  setErrorPopUp,
  auth,
}) => {
  const driveThruInfo = useMemo(() => {
    return getObjectValue(() => auth?.driveThruInfo, null);
  }, []);

  const getStoredSelection = key => {
    const val = window.sessionStorage.getItem(key);
    return val !== null && val !== 'null' && val !== '' ? Number(val) : null;
  };

  const [selectedLane, setSelectedLane] = useState(() =>
    getStoredSelection(STORAGE_KEYS.SELECTED_LANE)
  );

  const [selectedVehicleType, setSelectedVehicleType] = useState(() =>
    getStoredSelection(STORAGE_KEYS.SELECTED_VEHICLE_TYPE)
  );

  const [selectedVehicleColor, setSelectedVehicleColor] = useState(() =>
    getStoredSelection(STORAGE_KEYS.SELECTED_VEHICLE_COLOR)
  );

  // Handle check number change (Reset logic)
  useEffect(() => {
    if (!driveThruInfo || !check?.number) return;

    const currentCheck = String(check.number);
    const storedCheck = window.sessionStorage.getItem(
      STORAGE_KEYS.CURRENT_CHECK_NUMBER
    );

    if (currentCheck !== storedCheck) {
      window.sessionStorage.removeItem(STORAGE_KEYS.SELECTED_VEHICLE_TYPE);
      window.sessionStorage.removeItem(STORAGE_KEYS.SELECTED_VEHICLE_COLOR);
      window.sessionStorage.removeItem(STORAGE_KEYS.SELECTED_LANE);
      setSelectedVehicleType(null);
      setSelectedVehicleColor(null);
      setSelectedLane(null);
      window.sessionStorage.setItem(
        STORAGE_KEYS.CURRENT_CHECK_NUMBER,
        currentCheck
      );
    }
  }, [check?.number, driveThruInfo]);

  // Sync state with parent callback
  useEffect(() => {
    if (typeof onDriveThruSelectionChange !== 'function') return;

    try {
      onDriveThruSelectionChange({
        lane: typeof selectedLane === 'number' ? selectedLane : null,
        vehicleType:
          typeof selectedVehicleType === 'number' ? selectedVehicleType : null,
        vehicleColor:
          typeof selectedVehicleColor === 'number'
            ? selectedVehicleColor
            : null,
      });
    } catch (error) {
      setErrorPopUp?.({
        message: error?.message || 'Error updating drive thru selection',
        noErrorTxt: true,
      });
    }
  }, [
    selectedLane,
    selectedVehicleType,
    selectedVehicleColor,
    onDriveThruSelectionChange,
    setErrorPopUp,
  ]);

  const config = useMemo(() => {
    if (!driveThruInfo) return null;
    const {
      vehicleLaneMappings = [],
      vehicleTypeMappings = [],
      vehicleColorMappings = [],
      storeConfig = {},
    } = driveThruInfo;

    const laneCount = storeConfig.laneCount || 1;
    return {
      laneCount,
      displayedLanes:
        laneCount > 1 ? vehicleLaneMappings.slice(0, laneCount) : [],
      vehicleTypes: vehicleTypeMappings || [],
      vehicleColors: vehicleColorMappings || [],
    };
  }, [driveThruInfo]);

  const handleSelect = (key, setter, storageKey) => id => {
    try {
      setter(id);
      window.sessionStorage.setItem(storageKey, String(id));
    } catch (error) {
      setErrorPopUp?.({
        message: `Error selecting ${key.toLowerCase()}`,
        noErrorTxt: true,
      });
    }
  };

  if (!config) return null;

  return (
    <div className={style.driveThruMainContainer}>
      <LaneSelection
        displayedLanes={config.displayedLanes}
        selectedLane={selectedLane}
        onSelect={handleSelect(
          'Lane',
          setSelectedLane,
          STORAGE_KEYS.SELECTED_LANE
        )}
        serverAppTheme={serverAppTheme}
      />

      <div className="d-flex w-100">
        <div className={style.vehicleTypeColumn}>
          <VehicleInfo
            items={config.vehicleTypes}
            selectedId={selectedVehicleType}
            onSelect={handleSelect(
              'Vehicle Type',
              setSelectedVehicleType,
              STORAGE_KEYS.SELECTED_VEHICLE_TYPE
            )}
            laneCount={config.laneCount}
            hasLanes={config.displayedLanes.length > 0}
            serverAppTheme={serverAppTheme}
          />
        </div>

        <div className={style.vehicleColorColumn}>
          <ColorInfo
            colors={config.vehicleColors}
            selectedId={selectedVehicleColor}
            onSelect={handleSelect(
              'Vehicle Color',
              setSelectedVehicleColor,
              STORAGE_KEYS.SELECTED_VEHICLE_COLOR
            )}
            serverAppTheme={serverAppTheme}
            laneCount={config.laneCount}
            hasLanes={config.displayedLanes.length > 0}
          />
        </div>
      </div>
    </div>
  );
};
const mapStateToProps = ({ theme, oauth }) => ({
  serverAppTheme: theme.serverAppTheme,
  auth: oauth.authInfo,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ setErrorPopUp }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(DriveThruInfoV2);
